import { watch, type Ref, type WatchSource } from 'vue'
import type { AIChatSession } from '@/types/generated'

type SessionLike = AIChatSession | null | undefined

/**
 * Keep `selectedModelId` in sync with the current chat session's `model` field.
 *
 * - Chat switch (previous uuid differs from new uuid): always adopt the new
 *   session's model, overriding any prior selection.
 * - First load (no previous session, selection is null): adopt the persisted
 *   model from the session.
 * - Otherwise: leave the selection alone — this protects an in-flight user
 *   selection from being wiped when a new session is implicitly created by
 *   the send flow.
 *
 * Extracted from identical watchers in `AiChat.vue` and `SidePanelChat.vue`.
 */
export const useSessionModelSync = (
  sessionSource: WatchSource<SessionLike>,
  selectedModelId: Ref<string | null>
) => {
  watch(sessionSource, (session, oldSession) => {
    if (oldSession?.uuid && session?.uuid && session.uuid !== oldSession.uuid) {
      selectedModelId.value = session.model ?? null
    } else if (!oldSession && session && selectedModelId.value === null) {
      selectedModelId.value = session.model ?? null
    }
  })
}
